import {
  DownloadMembershipsParam,
  MembershipReport,
  SubscriptionTransaction,
  Subscription,
  toMembershipReport,
  toSubscription,
  toSubscriptionTransaction,
} from "./data/memberships";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR, { cache, mutate } from "swr";
import { useAuth0 } from "../core";
import { useI18n } from "../lib/i18n/i18n_provider";
import { chunk, isNotEmpty } from "../lib/js_utils/array_utils";
import { Order } from "../offering/api";
import { AddOn, toAddOn } from "./data/add_on";
import {
  BlockOutDate,
  BlockOutDateDetails,
  BlockOutDateInput,
  DateRangeParam,
  toBlockOutDate,
  toBlockOutDateDetails,
} from "./data/block_out_dates";
import { Booking, toBooking, BookingType } from "./data/booking";
import { Location, toLocation, toSimpleLocation } from "./data/location";
import {
  APIError,
  BookingsParam,
  CreateMultiPassBookingInput,
  fromOrderInput,
  ManageOrderItemsData,
  OrderInput,
  OrderViewOption,
  PartnerLocationOrderLineItemsResponse,
  PartnerUserProfile,
  toManageOrders,
  toMultiPassAccess,
  toOrder,
  toOrderItem,
  toOrderItemDayPassDetails,
  toPartnerCancelOrder,
  toPartnerUserProfile,
} from "./data/orders";
import {
  PartnerOffering,
  SpaceOffering,
  toPartnerOffering,
  toSpaceOffering,
} from "./data/partner_offering";
import {
  CreatePhysicalSpaceInput,
  FMPhysicalSpace,
  fromCreatePhysicalSpaceInput,
  fromPhysicalSpaceInput,
  toFMPhysicalSpace,
  PhysicalSpaceInput,
  useFMDayOfficePhysicalSpacesParams,
  BasePhysicalSpace,
  toBasePhysicalSpace,
} from "./data/physical_space";
import { Payout, Report, toPayoutData, toReport } from "./data/reports";
import {
  BaseSpace,
  Space,
  SpaceImage,
  SpaceInput,
  SpaceInventoriesInput,
  SpaceInventory,
  SpaceLayout,
  SpaceLayoutInput,
  SpaceOfferingsInput,
  SpacePricing,
  SpacePricingInput,
  SpacesSearchValue,
  fromSpaceInput,
  fromSpaceInventoriesInput,
  fromSpaceLayoutInput,
  fromSpaceOfferingInput,
  fromSpacePricingInput,
  toAmenity,
  toBaseSpace,
  toSpace,
  toSpaceInventory,
  toSpaceLayout,
  toSpacePricing,
  toSpaceImage,
} from "./data/space";
import {
  prepareCreateCampaignParams,
  prepareUpdateParams,
  Promotion,
  PromotionDetail,
  PromotionInput,
  PromotionStatus,
  toPromotion,
  toPromotionDetail,
} from "./data/promotion";
import { Paging, toPaging } from "./data/paging";
import { Promotable, PromotableType, toPromotable } from "./data/promotable";
import { toLightSpace } from "../data/api";
import {
  toMemberOfferingSpace,
  toSubscriptionBooking,
  toSubscriptionDetail,
} from "./data/entry_based_membership/data";
import { format } from "date-fns";
import {
  SuperAdminPartner,
  toSuperAdminPartner,
} from "./data/super_admin_partner";

export function usePartnerPost() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message || body.errors);
    },
    [token]
  );
}

export function usePartnerPut() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw body;
    },
    [token]
  );
}

function usePartnerDelete() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message);
    },
    [token]
  );
}

function usePartnerGet() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string) => {
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message);
    },
    [token]
  );
}

function usePartnerGetTextFile() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string) => {
      const response = await fetch(url, {
        headers: {
          "Content-Type": "text/csv;charset=utf-8;",
          Authorization: `Bearer ${token}`,
        },
      });
      const body = await response.text();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }
    },
    [token]
  );
}

function usePartnerFileUpload() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string, formData: FormData) => {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message || body.errors);
    },
    [token]
  );
}

function usePartnerPatch() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message || body.errors);
    },
    [token]
  );
}

export function useOrderUpdate(partnerID: string) {
  const put = usePartnerPut();

  return async (
    orderID: string,
    input: OrderInput
  ): Promise<Order | { error: any }> => {
    try {
      const data = fromOrderInput(input);

      const response = await put(
        `/api/v2/partners/${partnerID}/orders/${orderID}`,
        data
      );

      return response;
    } catch (error) {
      throw error;
    }
  };
}

export function useGetPartnerOfferingsMaybeQuery(
  partnerId?: string
): PartnerOffering[] {
  const response = useSWR(
    partnerId
      ? `/api/v3/partners/${partnerId}/offerings?exclude_marketing=true`
      : null,
    {
      revalidateOnFocus: false,
    }
  );

  return useMemo(
    () => (response.data ? response.data.data.map(toPartnerOffering) : []),
    [response.data]
  );
}

export function useGetAddOnsMaybeQuery(addOnIds: string[]): AddOn[] {
  const response = useSWR(
    addOnIds.length > 0 ? `/api/v3/add_ons?ids=${addOnIds.join(",")}` : null,
    {
      suspense: true,
      revalidateOnFocus: false,
    }
  );

  return useMemo(
    () =>
      response.data
        ? response.data.data
            .map(toAddOn)
            .sort((addOnA: AddOn, addOnB: AddOn) => {
              return (
                (addOnA.numberOfEntries || 0) - (addOnB.numberOfEntries || 0)
              );
            })
        : [],
    [response.data]
  );
}

export function useAccessAddOnQuery(partnerID: string, multiPassID: string) {
  const { data } = useSWR(
    `/api/v2/partners/${partnerID}/accesses/${multiPassID}`,
    {
      suspense: true,
    }
  );

  return toMultiPassAccess(data);
}

export function useAccessAddOnMaybeQuery(
  partnerID: string,
  multiPassID?: string
) {
  const { data } = useSWR(
    multiPassID
      ? `/api/v2/partners/${partnerID}/accesses/${multiPassID}`
      : null,
    {
      suspense: true,
    }
  );

  return data ? toMultiPassAccess(data) : data;
}

export function useCancelPartnerMultiPassBooking() {
  const post = usePartnerPost();

  return async (bookingID: string) => {
    const response = await post(`/api/v2/bookings/${bookingID}/cancel`);

    return response;
  };
}

export function useCreatePartnerMultiPassBooking() {
  const post = usePartnerPost();

  return async (
    partnerID: string,
    multiPassID: string,
    input: CreateMultiPassBookingInput
  ) => {
    const response = await post(
      `/api/v2/partners/${partnerID}/accesses/${multiPassID}/bookings`,
      input
    );

    return toOrderItemDayPassDetails(response);
  };
}

export function useCurrentPartnerUserProfileQuery(): PartnerUserProfile {
  const { data } = useSWR(`/api/v2/partner_user`, {
    suspense: true,
  });

  return useMemo(() => toPartnerUserProfile(data), [data]);
}

export const useGetSuperAdminPartners = () => {
  const { data } = useSWR<SuperAdminPartner[]>(`/api/v2/dashboard/partners`, {
    suspense: true,
  });

  return {
    data: data
      ? data?.map((item: SuperAdminPartner) => toSuperAdminPartner(item))
      : undefined,
  };
};

export const usePutSuperAdminPartner = () => {
  const put = usePartnerPut();

  return async (partnerId: string) => {
    const response = await put(`/api/v2/dashboard/partners/${partnerId}`);
    return toSuperAdminPartner(response);
  };
};

export function useGetPartnerLocationOrderLineItems(
  partnerID: string,
  param: BookingsParam,
  locationID?: string
): PartnerLocationOrderLineItemsResponse {
  const params = new URLSearchParams();
  if (param.startDate && param.endDate) {
    params.set("start_date", param.startDate);
    params.set("end_date", param.endDate);
  }
  if (param.viewOrderBy) {
    params.set(
      "by_usage_date",
      param.viewOrderBy === OrderViewOption.ByUsageDate ? "true" : "false"
    );
  }

  if (locationID) {
    params.set("location_id", locationID);
  }

  const qs = params.toString();

  const response = useSWR(
    `/api/v2/partners/${partnerID}/order_line_items?${qs}`,
    { suspense: true, revalidateOnFocus: false }
  );

  return useMemo(
    () => ({
      orderLineItems: response.data?.order_line_items
        ? response.data.order_line_items.map(toOrderItem)
        : [],
      lastUpdatedAt: response.data?.last_updated_at,
    }),
    [response.data]
  );
}

export function useGetManageOrdersPartnerOrderItems(
  partnerID: string,
  param: BookingsParam,
  locationID?: string
): ManageOrderItemsData {
  const params = new URLSearchParams();
  if (param.startDate && param.endDate) {
    params.set("start_date", param.startDate);
    params.set("end_date", param.endDate);
  }
  if (param.viewOrderBy) {
    params.set(
      "by_usage_date",
      param.viewOrderBy === OrderViewOption.ByUsageDate ? "true" : "false"
    );
  }

  if (locationID) {
    params.set("location_id", locationID);
  }

  if (param.page && param.perPage) {
    params.set("page", param.page.toString());
    params.set("per_page", param.perPage.toString());
  }

  if (param.searchText) {
    params.set("search_text", param.searchText);
  }

  if (param.productType) {
    params.set("product_type_id", param.productType);
  }

  const qs = params.toString();

  const response = useSWR(
    `/api/v3/dashboard/partners/${partnerID}/orders?${qs}`,
    { suspense: false, revalidateOnFocus: false }
  );

  return useMemo(
    () => ({
      ...(response.data
        ? toManageOrders(response.data)
        : {
            orders: undefined,
            paging: {
              currentPage: 1,
              totalPage: 0,
              perPage: 25,
              total: 0,
            },
          }),
      loading: !response.data && !response.error,
    }),
    [response.data, response.error]
  );
}

export function useDownloadOrders() {
  const get = usePartnerGet();

  return async (
    partnerID: string,
    param: BookingsParam,
    locationID?: string
  ) => {
    const params = new URLSearchParams();
    params.set("start_date", param.startDate);
    params.set("end_date", param.endDate);
    if (param.viewOrderBy) {
      params.set(
        "by_usage_date",
        param.viewOrderBy === OrderViewOption.ByUsageDate ? "true" : "false"
      );
    }
    if (locationID) {
      params.set("location_id", locationID);
    }
    const qs = params.toString();
    const response = get(
      `/api/v3/dashboard/partners/${partnerID}/orders/download_csv?${qs}`
    );
    return response;
  };
}

export function useDownloadOrdersV2() {
  const post = usePartnerPost();

  return async (
    partnerID: string,
    param: BookingsParam,
    locationID?: string
  ) => {
    const params = new URLSearchParams();
    if (param.startDate) {
      params.set("start_date", param.startDate);
    }
    if (param.endDate) {
      params.set("end_date", param.endDate);
    }
    if (param.viewOrderBy) {
      params.set(
        "by_usage_date",
        param.viewOrderBy === OrderViewOption.ByUsageDate ? "true" : "false"
      );
    }

    const qs = params.toString();
    const response = post(
      `/api/v3/dashboard/partners/${partnerID}/orders/schedule_to_download_csv?${qs}`
    );
    return response;
  };
}

export function useDownloadMembershipsOrTransactions() {
  const post = usePartnerPost();

  return async (partnerID: string, param: DownloadMembershipsParam) => {
    const params = new URLSearchParams();
    if (param.startDate) {
      params.set("start_date", param.startDate);
    }
    if (param.endDate) {
      params.set("end_date", param.endDate);
    }
    if (param.dataType) {
      params.set("data_type", param.dataType);
    }

    const qs = params.toString();
    const response = post(
      `/api/v3/dashboard/partners/${partnerID}/subscriptions/schedule_to_download_csv?${qs}`
    );
    return response;
  };
}

interface DownloadPayoutsParam {
  selectedMonth: string;
  filename: "on_demand_orders.csv" | "membership_orders.csv";
  payout_cost_center_id?: string;
}

export function useDownloadPayouts() {
  const get = usePartnerGetTextFile();

  return async (partnerID: string, param: DownloadPayoutsParam) => {
    const params = new URLSearchParams();

    if (param.payout_cost_center_id) {
      params.set("payout_cost_center_id", param.payout_cost_center_id);
    }

    const qs = params.toString();
    const response = get(
      `/api/v3/dashboard/partners/${partnerID}/payouts/${param.selectedMonth}/${param.filename}?${qs}`
    );

    return response;
  };
}

export function useDownloadFile() {
  const { token } = useAuth0();

  return useCallback(
    async (partnerId: string, fileName: string) => {
      const response = await fetch(
        `/api/v3/dashboard/partners/${partnerId}/download_files?file_name=${fileName}`,
        {
          headers: {
            "Content-Type": "text/csv",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        return response;
      }

      throw new APIError();
    },
    [token]
  );
}

export function useDownloadTransactions() {
  const { token } = useAuth0();

  return useCallback(
    async (partnerId: string, subscriptionId: string) => {
      const response = await fetch(
        `/api/v3/dashboard/partners/${partnerId}/subscriptions/${subscriptionId}/download_transactions_csv`,
        {
          headers: {
            "Content-Type": "text/csv",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        return response;
      }

      throw new APIError();
    },
    [token]
  );
}

export function useLocationMaybeQuery(locationID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data } = useSWR(
    locationID ? `/api/v2/locations/${locationID}${qs}` : null
  );

  return data ? toLocation(data) : undefined;
}

export function useLocationQuery(locationID: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data } = useSWR(`/api/v2/locations/${locationID}${qs}`, {
    suspense: true,
  });

  return toLocation(data);
}

export function useOrderQuery(orderID: string) {
  const { data } = useSWR(`/api/v2/orders/${orderID}`, {
    suspense: true,
  });

  return toOrder(data);
}

export function useOrderQueryV2(orderID: string) {
  const { data, error, isValidating } = useSWR(`/api/v2/orders/${orderID}`, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return {
    data: data ? toOrder(data) : undefined,
    loading: (!data && !error) || isValidating,
  };
}

export function usePartnerCancelOrder() {
  const post = usePartnerPost();

  return async (orderID: string, partnerID: string) => {
    const response = await post(
      `/api/v2/partners/${partnerID}/order/${orderID}/cancel`
    );

    return { data: response ? toPartnerCancelOrder(response) : undefined };
  };
}

export function usePartnerOrdersQuery(
  fetch: boolean,
  partnerID: string,
  orderIDs: string[]
) {
  const { token } = useAuth0();
  const response = useSWR(
    fetch ? [partnerID, orderIDs, token] : null,
    partnerOrdersFetcher,
    {
      suspense: false,
    }
  );

  return {
    data: response.data ? response.data.map(toOrder) : undefined,
    error: response.error,
  };
}

async function partnerOrdersFetcher(
  partnerID: string,
  orderIDs: string[],
  token: string
) {
  async function fetchOrder(orderID: string) {
    const res = await fetch(`/api/v2/partners/${partnerID}/orders/${orderID}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const body = await res.json();

    if (res.status >= 200 && res.status < 300) {
      return body;
    }

    throw new APIError(body.message);
  }

  let orders: Order[];

  const data = await Promise.all(
    orderIDs.map((id) => {
      return fetchOrder(id);
    })
  );

  orders = data.flat();

  return orders;
}

export function usePartnerOrderQuery(partnerID: string, orderID: string) {
  const { data } = useSWR(`/api/v2/partners/${partnerID}/orders/${orderID}`, {
    suspense: true,
  });

  return toOrder(data);
}

export function usePartnerOrderQueryV2(partnerID: string, orderID: string) {
  const { data, error, isValidating } = useSWR(
    `/api/v2/partners/${partnerID}/orders/${orderID}`,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    data: data ? toOrder(data) : undefined,
    loading: (!data && !error) || isValidating,
  };
}

export function useSpaceQuery(spaceID: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data } = useSWR(`/api/v2/spaces/${spaceID}${qs}`, { suspense: true });

  return toSpace(data);
}

/**
 * Fetch spaces with loading
 */
export function useSpacesQueryV3(spaceIDs: string[], includes?: string) {
  const { locale } = useI18n();
  const { token } = useAuth0();

  const { data, error } = useSWR(
    isNotEmpty(spaceIDs)
      ? `/api/v2/spaces?ids=${spaceIDs.join(",")}&locale=${
          locale.languageTag
        }&includes=${includes}`
      : null,
    () => fetchSpaces(spaceIDs, locale.languageTag, token, includes)
  );

  return {
    data: data ? (data as Space[]) : undefined,
    loading: spaceIDs && !data && !error,
    error,
  };
}

/**
 * Fetch spaces and "guarantee" a response without `loading` boolean
 */
export function useSpacesQuery(spaceIDs: string[], includes?: string): Space[] {
  const { locale } = useI18n();
  const { token } = useAuth0();

  const response = useSWR(
    isNotEmpty(spaceIDs)
      ? `/api/v2/spaces?ids=${spaceIDs.join(",")}&locale=${
          locale.languageTag
        }&includes=${includes}`
      : null,
    () => fetchSpaces(spaceIDs, locale.languageTag, token, includes),
    { suspense: true }
  );

  return response.data as Space[];
}

async function fetchSpaces(
  spaceIDs: string[],
  languageTag: string,
  token: string,
  includes?: string
) {
  let spaces: Space[] = [];

  if (!spaceIDs || spaceIDs.length === 0) {
    return spaces;
  }

  await Promise.all(
    // Fetch max 50 spaces at a time (max params length is 36*50=1800 )
    chunk(spaceIDs, 50).map(async (chunkSpaceIds) => {
      const res = await fetch(
        `/api/v2/spaces?ids=${chunkSpaceIds.join(
          ","
        )}&locale=${languageTag}&includes=${includes}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const body = await res.json();
      spaces = spaces.concat(body.map(toSpace));
    })
  );

  return spaces;
}

export function useSpaceMaybeQuery(spaceID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data } = useSWR(spaceID ? `/api/v2/spaces/${spaceID}${qs}` : null, {
    suspense: true,
  });

  return data ? toSpace(data) : data;
}

export function useCreatePhysicalSpace(partnerID: string) {
  const post = usePartnerPost();

  return async (input: CreatePhysicalSpaceInput): Promise<FMPhysicalSpace> => {
    const data = fromCreatePhysicalSpaceInput(input);
    const response = await post(
      `/api/v2/partners/${partnerID}/physical_spaces`,
      data
    );

    return toFMPhysicalSpace(response.physical_space);
  };
}

export function useUpdatePhysicalSpace(partnerID: string) {
  const put = usePartnerPut();

  return async (
    physicalSpaceID: string,
    input: PhysicalSpaceInput
  ): Promise<FMPhysicalSpace> => {
    const data = fromPhysicalSpaceInput(input);

    const response = await put(
      `/api/v2/partners/${partnerID}/physical_spaces/${physicalSpaceID}`,
      data
    );

    return toFMPhysicalSpace(response.physical_space);
  };
}

export function useFMDayOfficePhysicalSpaceQuery(
  partnerID: string,
  physicalSpaceID: string
): FMPhysicalSpace {
  const response = useSWR(
    `/api/v2/partners/${partnerID}/physical_spaces/${physicalSpaceID}`,
    { suspense: true }
  );

  return toFMPhysicalSpace(response.data);
}

export function useFMDayOfficePhysicalSpaces(
  partnerID: string,
  locationID: string,
  params: useFMDayOfficePhysicalSpacesParams
): FMPhysicalSpace[] {
  const response = useSWR(
    `/api/v2/partners/${partnerID}/physical_spaces?space_type=private_office&location_id=${locationID}&physical_space_start_date=${params.startDate}&physical_space_end_date=${params.endDate}`,
    { suspense: true }
  );

  return response.data.map(toFMPhysicalSpace);
}

export function useGetSpacePhysicalSpaces(spaceID: string): FMPhysicalSpace[] {
  const response = useSWR(() =>
    spaceID ? `/api/spaces/${spaceID}/physical_spaces` : null
  );

  if (!response.data) {
    return [];
  }

  return response.data.map(toFMPhysicalSpace);
}

export function useCreatePhysicalSpaceV2(partnerID: string, spaceID: string) {
  const post = usePartnerPost();

  return async (input: PhysicalSpaceInput): Promise<BasePhysicalSpace> => {
    const data = fromPhysicalSpaceInput(input);

    const response = await post(
      `/api/v3/dashboard/partners/${partnerID}/physical_spaces?space_id=${spaceID}`,
      data
    );

    return toBasePhysicalSpace(response.data);
  };
}

export function useUpdatePhysicalSpaceV2(
  partnerID: string,
  spaceID: string,
  physicalSpaceID?: string
) {
  const put = usePartnerPut();

  return async (input: PhysicalSpaceInput): Promise<BasePhysicalSpace> => {
    const data = fromPhysicalSpaceInput(input);

    const response = await put(
      `/api/v3/dashboard/partners/${partnerID}/physical_spaces/${physicalSpaceID}?space_id=${spaceID}`,
      data
    );

    return toBasePhysicalSpace(response.data);
  };
}

function fromBlockOutDateInput(input: BlockOutDateInput) {
  return {
    start_date: input.startDate,
    end_date: input.endDate,
    reason: input.reason,
    blockable_type: input.blockableType,
    blockable_id: input.blockableID,
  };
}

export function useUpdateBlockOutDate(partnerID: string) {
  const put = usePartnerPut();

  return async (
    blockOutDateID: string,
    input: BlockOutDateInput
  ): Promise<BlockOutDate> => {
    const data = fromBlockOutDateInput(input);

    const response = await put(
      `/api/v2/partners/${partnerID}/block_out_dates/${blockOutDateID}`,
      data
    );

    return toBlockOutDate(response);
  };
}

export function useAddBlockOutDate(partnerID: string) {
  const post = usePartnerPost();

  return async (input: BlockOutDateInput): Promise<BlockOutDate> => {
    const data = fromBlockOutDateInput(input);

    const response = await post(
      `/api/v2/partners/${partnerID}/block_out_dates`,
      data
    );

    return toBlockOutDate(response);
  };
}

export function useUpdateSpacePricing(
  partnerID: string,
  spacePricingID: string
) {
  const put = usePartnerPut();

  return async (input: SpacePricingInput): Promise<SpacePricing> => {
    const data = fromSpacePricingInput(input);

    const response = await put(
      `/api/v3/dashboard/partners/${partnerID}/space_pricings/${spacePricingID}`,
      data
    );

    return toSpacePricing(response);
  };
}

export function useSpacePricingQuery(
  partnerID: string,
  spacePricingID: string
) {
  const response = useSWR(
    `/api/v3/dashboard/partners/${partnerID}/space_pricings/${spacePricingID}`
  );

  return response.data ? toSpacePricing(response.data.data) : null;
}

export function useUpdateSpaceLayout(partnerID: string, spaceLayoutID: string) {
  const put = usePartnerPut();

  return async (input: SpaceLayoutInput): Promise<SpaceLayout> => {
    const data = fromSpaceLayoutInput(input);

    const response = await put(
      `/api/v3/dashboard/partners/${partnerID}/space_layouts/${spaceLayoutID}`,
      data
    );

    return toSpaceLayout(response);
  };
}

export function useUpdateSpaceInventories(partnerID: string, spaceID: string) {
  const put = usePartnerPut();

  return async (input: SpaceInventoriesInput): Promise<SpaceInventory> => {
    const data = fromSpaceInventoriesInput(input);

    const response = await put(
      `/api/v3/dashboard/partners/${partnerID}/spaces/${spaceID}/inventory_update`,
      data
    );

    return toSpaceInventory(response);
  };
}

export function useUpdateSpace(partnerID: string, spaceID: string) {
  const put = usePartnerPut();

  return async (input: SpaceInput): Promise<BaseSpace> => {
    const data = fromSpaceInput(input);

    const response = await put(
      `/api/v3/dashboard/partners/${partnerID}/spaces/${spaceID}`,
      data
    );

    return toBaseSpace(response.data);
  };
}

export function useCheckActivableSpace(partnerID: string, spaceID: string) {
  const { mutate } = useSWR(
    `/api/v3/dashboard/partners/${partnerID}/spaces/${spaceID}/activable`
  );

  return { mutate };
}

export function useLocationAmenitiesQuery(locationID: string) {
  let qs = "";
  const { locale } = useI18n();
  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    locationID ? `/api/v2/locations/${locationID}/amenities${qs}` : null
  );

  return {
    data: data ? data.map(toAmenity) : undefined,
    loading: locationID && !data && !error,
    error,
  };
}

export function useSpaceAmenitiesQueryV2(partnerID: string, spaceID: string) {
  let qs = "";
  const { locale } = useI18n();
  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    spaceID
      ? `/api/v3/dashboard/partners/${partnerID}/spaces/${spaceID}/amenities${qs}`
      : null
  );

  return {
    data: data ? data.map(toAmenity) : undefined,
    loading: spaceID && !data && !error,
    error,
  };
}

interface SpacesQueryParams {
  partnerOfferingSlug: string;
  locationID: string;
  pagination: {
    page: number;
    perPage: number;
  };
  searchByName?: string;
}

function toSpacesQueryParams(params: SpacesQueryParams) {
  const searchParams = new URLSearchParams();

  searchParams.set("offering_slug", params.partnerOfferingSlug);
  searchParams.set("location_id", params.locationID);
  if (params.searchByName) {
    searchParams.set("search_by_name", params.searchByName);
  }
  searchParams.set("page", params.pagination.page.toString());
  searchParams.set("per_page", params.pagination.perPage.toString());

  return `?${searchParams.toString()}`;
}

export function useSpacesQueryV2(partnerID: string, params: SpacesQueryParams) {
  const searchParams = toSpacesQueryParams(params);
  const { data, error } = useSWR(
    partnerID
      ? `/api/v3/dashboard/partners/${partnerID}/spaces${searchParams.toString()}`
      : null
  );

  return {
    data: data ? data.data.map(toBaseSpace) : [],
    paging: data?.paging ? toPaging(data?.paging) : {},
    loading: partnerID && !data && !error,
    error,
  };
}

export function useDeleteBlockOutDate() {
  const partnerDelete = usePartnerDelete();

  return async (partnerID: string, blockOutDateID: string) => {
    const response = await partnerDelete(
      `/api/v2/partners/${partnerID}/block_out_dates/${blockOutDateID}`
    );

    return response;
  };
}

export function useGetBlockOutDatesQuery(
  partnerID: string,
  param: DateRangeParam,
  locationID: string
): BlockOutDate[] {
  const params = new URLSearchParams();
  params.set("start_date", param.startDate);
  params.set("end_date", param.endDate);
  if (locationID) {
    params.set("location_id", locationID);
  }

  const qs = params.toString();

  const { data } = useSWR(
    `/api/v2/partners/${partnerID}/block_out_dates?${qs}`
  );

  return data ? data.map(toBlockOutDate) : [];
}

export function useFMBlockOutDateQuery(
  partnerID: string,
  blockOutDateID: string
): BlockOutDateDetails {
  const data = useSWR(
    `/api/v2/partners/${partnerID}/block_out_dates/${blockOutDateID}`,
    { suspense: true }
  );
  return data ? toBlockOutDateDetails(data.data) : data;
}

export function useGetPartnerLocationBookingsMaybeQuery(
  partnerID: string,
  locationID?: string,
  param?: BookingsParam
) {
  const response = useSWR(
    locationID
      ? `/api/v2/bookings?partner_id=${partnerID}&location_id=${locationID}&start_date=${param?.startDate}&end_date=${param?.endDate}`
      : null
  );

  return response
    ? {
        data: (response.data ? response.data.map(toBooking) : []) as Booking[],
        loading: !response,
      }
    : { data: response };
}

export function useGetPartnerBookingsMaybeQuery(
  partnerID: string,
  param: BookingsParam
) {
  const response = useSWR(
    partnerID
      ? `/api/v3/dashboard/partners/${partnerID}/bookings?start_date=${param?.startDate}&end_date=${param?.endDate}`
      : null
  );

  return response
    ? {
        data: (response.data?.data
          ? response.data.data.map(toBooking)
          : []) as Booking[],
        loading: !response,
      }
    : { data: response };
}

export function useLocationSpacesQuery(
  locationID?: string,
  searchValue?: SpacesSearchValue
): Space[] {
  const searchParams = new URLSearchParams();

  let qs = "";

  if (searchValue) {
    if (searchValue.attendees) {
      searchParams.set("min_capacity", String(searchValue.attendees));
    }

    if (searchValue.dateTime) {
      if (
        searchValue.dateTime.bookingType === BookingType.Hourly &&
        searchValue.dateTime.date &&
        searchValue.dateTime.time &&
        searchValue.dateTime.duration
      ) {
        searchParams.set("date", searchValue.dateTime.date);
        searchParams.set("time", searchValue.dateTime.time);
        searchParams.set("duration", searchValue.dateTime.duration);
      } else if (
        searchValue.dateTime.bookingType === BookingType.Daily &&
        searchValue.dateTime.startDate &&
        searchValue.dateTime.endDate
      ) {
        searchParams.set("start_date", searchValue.dateTime.startDate);
        searchParams.set("end_date", searchValue.dateTime.endDate);

        if (searchValue.dateTime.time) {
          searchParams.set("time", searchValue.dateTime.time);
        }
      }
    }

    if (searchValue.spaceType) {
      searchParams.set("space_type", searchValue.spaceType);
    }

    qs = `?${searchParams.toString()}`;
  }

  const response = useSWR(
    locationID ? `/api/v2/locations/${locationID}/spaces${qs}` : null
  );

  return response.data ? response.data.map(toSpace) : [];
}

export function useSpaceAmenitiesQuery(spaceID?: string | null) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    spaceID ? `/api/v2/spaces/${spaceID}/amenities${qs}` : null
  );

  return {
    data: data ? data.map(toAmenity) : undefined,
    loading: spaceID && !data && !error,
    error,
  };
}

export function useUpdateSpaceOfferings(partnerID: string) {
  const put = usePartnerPut();

  return async (
    spaceOfferingId: string,
    input: SpaceOfferingsInput
  ): Promise<SpaceOffering> => {
    const data = fromSpaceOfferingInput(input);

    const response = await put(
      `/api/v3/dashboard/partners/${partnerID}/space_offerings/${spaceOfferingId}`,
      data
    );

    return toSpaceOffering(response);
  };
}

export function useSpaceOfferingsQuery(
  spaceID?: string,
  whitelistedAddOnIds?: string[]
): {
  error: any;
  loading: boolean;
  data: SpaceOffering[];
} {
  const searchParams = new URLSearchParams();
  const { locale } = useI18n();

  searchParams.append("locale", locale.languageTag);

  const { data, error } = useSWR(
    spaceID
      ? `/api/v2/spaces/${spaceID}/space_offerings?${searchParams.toString()}`
      : null
  );

  return {
    error: error || "",
    loading: !!(spaceID && !data),
    data: data
      ? data
          .map(toSpaceOffering)
          .filter(
            (so: SpaceOffering) =>
              !whitelistedAddOnIds ||
              whitelistedAddOnIds.length === 0 ||
              whitelistedAddOnIds.includes(so.addOnID)
          )
          .sort((offeringA: SpaceOffering, offeringB: SpaceOffering) => {
            return (
              (offeringA.numberOfEntries || 0) -
              (offeringB.numberOfEntries || 0)
            );
          })
      : undefined,
  };
}

export function useGetPartnerReport(
  partnerID: string,
  startDate: string,
  endDate: string,
  locationID?: string | null
): Report {
  let endpoint = `/api/v2/partners/${partnerID}/reports?start_date=${startDate}&end_date=${endDate}`;
  if (locationID) {
    endpoint += `&location_ids=${locationID}`;
  }
  const response = useSWR(endpoint, { suspense: true });

  return toReport(response.data.reports);
}

export function useLocationsQuery(locationIDs: string[]) {
  const response = useSWR([locationIDs], locationsFetcher);
  const [cache, setCache] = useState(response.data || []);

  useEffect(() => {
    if (response.data) {
      setCache(response.data);
    }
  }, [response.data]);
  return {
    data: cache,
    loading: !response.data,
  };
}

async function locationsFetcher(locationIDs: string[]) {
  async function fetchLocation(id: string) {
    const res = await fetch(`/api/v2/locations/${id}`);

    const body = await res.json();

    if (res.status >= 200 && res.status < 300) {
      return body;
    }

    throw new APIError(body.message);
  }

  let locations: Location[] = [];

  const data = await Promise.all(locationIDs.map((id) => fetchLocation(id)));

  locations = data.map(toLocation);

  return locations;
}

/* Invalidate cache */
export async function invalidateOrderCache(orderID: string) {
  const keys = cache.keys();
  // Delete any cache that includes the orderID in its key
  const orderIDKeys = keys.filter((k) => k.includes(orderID));

  for (let k of orderIDKeys) {
    await mutate(k);
  }
}

export async function invalidateOrderLineItemCache(orderItemID?: string) {
  if (!orderItemID) return;
  const keys = cache.keys();
  // Delete any cache that includes the orderItemID in its key
  let orderItemIDKeys = keys.filter((k) => k.includes(orderItemID));

  orderItemIDKeys = orderItemIDKeys.concat(
    keys.filter((k) => k.includes("order_line_items"))
  );

  for (let k of orderItemIDKeys) {
    await mutate(k);
  }
}

export async function invalidateMultiPassAccessCache(multiPassID?: string) {
  if (!multiPassID) return;
  const keys = cache.keys();
  // Delete any cache that includes the multiPassID in its key
  const spaceKeys = keys.filter((k) => k.includes(multiPassID));

  for (let k of spaceKeys) {
    await mutate(k);
  }
}

export async function invalidatePhysicalSpaceCache() {
  const keys = cache.keys();
  // Delete any cache that includes the physical_spaces in its key
  const physicalSpacesKeys = keys.filter((k) => k.includes("physical_spaces"));

  for (let k of physicalSpacesKeys) {
    await mutate(k);
  }
}

export async function invalidatePhysicalSpacesCache(spaceID?: string) {
  if (!spaceID) return;
  const keys = cache.keys();
  // Delete any cache that includes the spaceID and physical_spaces in its key
  let spaceIDKeys = keys.filter((k) => k.includes(spaceID));

  spaceIDKeys = spaceIDKeys.concat(
    keys.filter((k) => k.includes("physical_spaces"))
  );
  for (let k of spaceIDKeys) {
    await mutate(k);
  }
}

export async function invalidateBlockOutDateCache() {
  const keys = cache.keys();
  // Delete any cache that includes the block_out_dates in its key
  const blockOutDateKeys = keys.filter((k) => k.includes("block_out_dates"));

  for (let k of blockOutDateKeys) {
    await mutate(k);
  }
}

export async function invalidateBookingsCache() {
  const keys = cache.keys();
  // Delete any cache that includes the bookings in its key
  const bookingsKeys = keys.filter((k) => k.includes("bookings"));

  for (let k of bookingsKeys) {
    await mutate(k);
  }
}

export async function invalidateSpacePricingsCache() {
  const keys = cache.keys();
  // Delete any cache that includes the space_pricings in its key
  const spacePricingsKeys = keys.filter((k) => k.includes("space_pricings"));

  for (let k of spacePricingsKeys) {
    await mutate(k);
  }
}

export async function invalidateOrdersCache() {
  const keys = cache.keys();
  // Delete any cache that includes the orders in its key
  const spacePricingsKeys = keys.filter((k) => k.includes("orders"));

  for (let k of spacePricingsKeys) {
    await mutate(k);
  }
}

interface PromotionsParams {
  term: string;
  status: PromotionStatus | undefined;
  locationId: string | null;
  page: number;
  perPage: number;
}

function toPromotionsParams(params: PromotionsParams) {
  const searchParams = new URLSearchParams();

  if (params.term) {
    searchParams.set("term", params.term);
  }
  if (PromotionStatus.Active === params.status) {
    searchParams.set("active", "true");
  } else if (PromotionStatus.Expired === params.status) {
    searchParams.set("active", "false");
  }
  if (params.locationId) {
    searchParams.set("location_id", params.locationId);
  }
  searchParams.set("page", params.page.toString());
  searchParams.set("per_page", params.perPage.toString());

  return `?${searchParams.toString()}`;
}

export function usePromotionsQuery(
  partnerId: string | null,
  params: PromotionsParams
) {
  const { data, error } = useSWR(
    partnerId
      ? `/api/v3/dashboard/partners/${partnerId}/promotions${toPromotionsParams(
          params
        )}`
      : null
  );

  return {
    data: data ? data.data.map(toPromotion) : [],
    paging: data?.paging ? toPaging(data?.paging) : {},
    loading: partnerId && !data && !error,
    error,
  };
}

export function usePartnerLocationsQuery(
  partnerId: string,
  page = 1,
  perPage = 10000
) {
  let qs = `?page=${page}&per_page=${perPage}`;

  const { data, error } = useSWR(
    partnerId ? `/api/v3/dashboard/partners/${partnerId}/locations${qs}` : null
  );

  return {
    data: data ? data.data.map(toSimpleLocation) : [],
    paging: data?.paging ? toPaging(data?.paging) : {},
    loading: partnerId && !data && !error,
    error,
  };
}

interface PromotablesParams {
  promotableType: PromotableType;
  locationId?: string | null;
}

function toPromotablesParams(params: PromotablesParams) {
  const searchParams = new URLSearchParams();

  if (params.promotableType) {
    searchParams.set("promotable_type", params.promotableType);
  }
  if (params.locationId) {
    searchParams.set("location_id", params.locationId);
  }

  return `?${searchParams.toString()}`;
}

export function usePromotablesQuery(
  partnerId: string,
  params: PromotablesParams
): {
  data: Array<Promotable>;
  loading: boolean;
  error: any;
} {
  const { data, error } = useSWR(
    partnerId
      ? `/api/v3/dashboard/partners/${partnerId}/promotables${toPromotablesParams(
          params
        )}`
      : null
  );

  return {
    data: data ? data.data.map(toPromotable) : [],
    loading: !!partnerId && !data && !error,
    error,
  };
}

export function useCreateCampaign(partnerId: string) {
  const post = usePartnerPost();

  return async (input: PromotionInput): Promise<Promotion> => {
    const data = prepareCreateCampaignParams(input);

    const response = await post(
      `/api/v3/dashboard/partners/${partnerId}/promotions`,
      data
    );

    return toPromotion(response.data);
  };
}

export function useGetCampaignQuery(
  partnerId: string,
  campaignId?: string
): PromotionDetail | null {
  const response = useSWR(
    campaignId
      ? `/api/v3/dashboard/partners/${partnerId}/promotions/${campaignId}`
      : null,
    { suspense: true }
  );

  return response ? toPromotionDetail(response.data.data) : null;
}

export function usePutCampaign(partnerId: string) {
  const put = usePartnerPut();

  return async (
    promotionDetail: PromotionDetail,
    input: PromotionInput
  ): Promise<Promotion> => {
    const data = prepareUpdateParams(promotionDetail, input);

    const response = await put(
      `/api/v3/dashboard/partners/${partnerId}/promotions/${promotionDetail.id}`,
      data
    );

    return toPromotion(response.data);
  };
}

interface SubscriptionsParams {
  startDate?: string;
  endDate?: string;
  searchText?: string;
  locationID?: string | null;
  page?: number;
  perPage?: number;
  status?: string;
  productType?: string;
}

function toSubscriptionsParams(params: SubscriptionsParams) {
  const searchParams = new URLSearchParams();

  if (params.startDate) {
    searchParams.set("start_date", params.startDate);
  }
  if (params.endDate) {
    searchParams.set("end_date", params.endDate);
  }
  if (params.locationID) {
    searchParams.set("location_id", params.locationID);
  }
  if (params.searchText) {
    searchParams.set("search_text", params.searchText);
  }
  if (params.page) {
    searchParams.set("page", params.page.toString());
  }
  if (params.perPage) {
    searchParams.set("per_page", params.perPage.toString());
  }

  if (params.status) {
    searchParams.set("status", params.status);
  }
  if (params.productType) {
    searchParams.set("product_type_id", params.productType);
  }

  return `?${searchParams.toString()}`;
}

export function useSubscriptionsQuery(
  partnerId: string | null,
  params: SubscriptionsParams
): {
  data: Subscription[];
  paging?: Paging;
  loading: boolean;
  error: APIError;
  revalidate: () => Promise<boolean>;
} {
  const { data, error, revalidate } = useSWR(
    partnerId
      ? `/api/v3/dashboard/partners/${partnerId}/subscriptions${toSubscriptionsParams(
          params
        )}`
      : null
  );

  return {
    data: data ? data.data.map(toSubscription) : [],
    paging: data?.paging ? toPaging(data?.paging) : undefined,
    loading: !!partnerId && !data && !error,
    error,
    revalidate,
  };
}

interface MembershipReportsParams {
  startDate: string;
  endDate: string;
  locationID?: string | null;
}

function toMembershipReportsParams(params: MembershipReportsParams) {
  const searchParams = new URLSearchParams();

  if (params.startDate) {
    searchParams.set("start_date", params.startDate);
  }
  if (params.endDate) {
    searchParams.set("end_date", params.endDate);
  }
  if (params.locationID) {
    searchParams.set("location_id", params.locationID);
  }
  return `?${searchParams.toString()}`;
}

export function useGetMembershipReports(
  partnerID: string,
  params: MembershipReportsParams
): MembershipReport {
  let endpoint = `/api/v3/dashboard/partners/${partnerID}/membership_reports${toMembershipReportsParams(
    params
  )}`;
  const response = useSWR(endpoint, { suspense: true });

  return toMembershipReport(response.data);
}

export function useDownloadMemberships() {
  const get = usePartnerGet();

  return async (partnerId: string, params: SubscriptionsParams) => {
    const response = get(
      `/api/v3/dashboard/partners/${partnerId}/subscriptions/download_csv${toSubscriptionsParams(
        params
      )}`
    );

    return response;
  };
}

export function useSpacesWithLocationProductTypeQuery(
  locationId: string,
  productType: string
) {
  const response = useSWR(
    locationId
      ? `/api/v2/locations/${locationId}/spaces?space_type=${productType}&light_response=true`
      : null
  );
  return response.data ? response.data.map(toLightSpace) : [];
}

export function useDashboardSubscriptionDetailsQuery(
  subscriptionId: string,
  partnerId: string
) {
  const { data, isValidating, revalidate, error } = useSWR(
    `/api/v3/dashboard/partners/${partnerId}/subscriptions/${subscriptionId}`,
    { revalidateOnFocus: false }
  );

  return {
    data: data?.data ? toSubscriptionDetail(data?.data) : undefined,
    loading: isValidating || (!data && !error),
    revalidate,
  };
}

export function useDashboardSubscriptionSpacesQuery(
  subscriptionId: string,
  partnerId: string
) {
  const { data, isValidating, error } = useSWR(
    `/api/v3/dashboard/partners/${partnerId}/subscriptions/${subscriptionId}/offering_spaces`,
    { revalidateOnFocus: false }
  );

  return {
    data: data?.data ? data.data.map(toMemberOfferingSpace) : undefined,
    loading: isValidating || (!data && !error),
  };
}

export function useDashboardSubscriptionBookingsQuery(
  subscriptionId: string,
  partnerId: string
) {
  const { data, isValidating, revalidate, error } = useSWR(
    `/api/v3/dashboard/partners/${partnerId}/bookings?subscription_id=${subscriptionId}&booking_type=membership`,
    { revalidateOnFocus: false }
  );

  return {
    data: data?.data ? data.data?.map(toSubscriptionBooking) : undefined,
    loading: isValidating || (!data && !error),
    revalidate,
  };
}

interface CreateMembershipBookingInput {
  spaceId: string;
  startDate: Date;
  endDate: Date;
}

export function useDashboardCreateMembershipBooking() {
  const post = usePartnerPost();

  return async (
    partnerId: string,
    subscriptionId: string,
    input: CreateMembershipBookingInput
  ) => {
    await post(`/api/v3/dashboard/partners/${partnerId}/bookings`, {
      subscription_id: subscriptionId,
      space_id: input.spaceId,
      start_date: format(input.startDate, "yyyy-MM-dd"),
      end_date: format(input.endDate, "yyyy-MM-dd"),
    });
  };
}

export function useDashboardCancelSubscriptionBooking() {
  const cancel = usePartnerDelete();

  return async (partnerId: string, bookingId: string) => {
    await cancel(
      `/api/v3/dashboard/partners/${partnerId}/bookings/${bookingId}`
    );
  };
}

interface useDashboardSubscriptionTransactionsQueryReturn {
  data?: {
    transactions: SubscriptionTransaction[];
    paging: Paging;
  };
  loading: boolean;
  revalidate: () => Promise<boolean>;
}

export function useDashboardSubscriptionTransactionsQuery(
  subscriptionId: string,
  partnerId: string
): useDashboardSubscriptionTransactionsQueryReturn {
  const { data, isValidating, revalidate, error } = useSWR(
    `/api/v3/dashboard/partners/${partnerId}/subscriptions/${subscriptionId}/transactions`,
    { revalidateOnFocus: false }
  );

  return {
    data: data?.data
      ? {
          transactions: data.data.map(toSubscriptionTransaction),
          paging: toPaging(data?.paging),
        }
      : undefined,
    loading: isValidating || (!data && !error),
    revalidate,
  };
}

export function useGetPayoutsData(
  partnerId: string,
  selectedMonth: string
): {
  data?: Payout[];
  loading: boolean;
} {
  const { data, isValidating, error } = useSWR(
    partnerId
      ? `/api/v3/dashboard/partners/${partnerId}/payouts/${selectedMonth}`
      : null,
    { revalidateOnFocus: false }
  );

  return {
    data: data ? data.map(toPayoutData) : undefined,
    loading: isValidating || (!data && !error),
  };
}

export function invalidateSpacesQueryV3Cache(spaceID: string) {
  const keys = cache.keys();
  // Delete any cache that includes the spaceID in its key
  const spaceKeys = keys.filter((k) => k.includes(spaceID));

  for (let k of spaceKeys) {
    void mutate(k);
  }
}

export function useGetMarketingReportUrl(partnerId: string): {
  data?: string;
  loading: boolean;
} {
  const { data, isValidating, error } = useSWR(
    partnerId
      ? `/api/v3/dashboard/partners/${partnerId}/marketing_report`
      : null,
    { revalidateOnFocus: false }
  );

  return {
    data: data?.data,
    loading: isValidating || (!data && !error),
  };
}

export function useUploadSpaceImages() {
  const uploadFile = usePartnerFileUpload();

  return useCallback(
    async (
      partnerID: string,
      spaceID: string,
      images: File[]
    ): Promise<{ images: SpaceImage[] }> => {
      const formData = new FormData();
      images.forEach((file) => {
        formData.append("images[]", file);
      });
      formData.append("space_id", spaceID);

      const response = await uploadFile(
        `/api/v3/dashboard/partners/${partnerID}/space_images`,
        formData
      );

      return { images: response.data.map(toSpaceImage) };
    },
    [uploadFile]
  );
}

export function useUpdateSpaceImageSortOrders() {
  const patch = usePartnerPatch();

  return useCallback(
    async (
      partnerID: string,
      spaceImages: Array<{ id: string; imageOrder: number }>
    ) => {
      return await patch(
        `/api/v3/dashboard/partners/${partnerID}/space_images/bulk_update`,
        {
          images: spaceImages.map((img) => ({
            id: img.id,
            image_order: img.imageOrder,
          })),
        }
      );
    },
    [patch]
  );
}

export function useDeleteSpaceImages() {
  const deleteRequest = usePartnerDelete();

  return useCallback(
    async (partnerID: string, spaceID: string, imageIds: string[]) => {
      return await deleteRequest(
        `/api/v3/dashboard/partners/${partnerID}/space_images/bulk_delete`,
        {
          image_ids: imageIds,
          space_id: spaceID,
        }
      );
    },
    [deleteRequest]
  );
}

export function useSpaceQueryCallBack() {
  const get = usePartnerGet();
  return useCallback(
    async (spaceID: string) => {
      const response = await get(`/api/v2/spaces/${spaceID}`);
      return toSpace(response);
    },
    [get]
  );
}
